import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Users, Target, TrendingUp, Sparkles } from 'lucide-react';
import { clsx } from 'clsx';
import {
  creativeWorkbenchApi,
  type WorkbenchPersonaDetailResponse,
  type WorkbenchPersonaDetailAd,
  type WorkbenchVerdict,
} from '@/services/api';
import { useCreativeWorkbench } from '../CreativeWorkbenchContext';
import { FormulaAffinityPanel } from './FormulaAffinityPanel';
import { AdDetailDrawer } from './AdDetailDrawer';
import { EmbeddedChatPanel, type ChatSeedInput } from './EmbeddedChatPanel';

const CURRENCY = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const VERDICT_BADGE: Record<WorkbenchVerdict, string> = {
  strong: 'bg-emerald-100 text-emerald-700',
  promising: 'bg-teal-100 text-teal-700',
  needs_work: 'bg-amber-100 text-amber-700',
  misaligned: 'bg-red-100 text-red-700',
};

export function PersonaDetailView() {
  const { personaId } = useParams<{ personaId: string }>();
  const navigate = useNavigate();
  const { clientUid, dateRange, setPersona } = useCreativeWorkbench();
  const [data, setData] = useState<WorkbenchPersonaDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [drawerAdId, setDrawerAdId] = useState<string | null>(null);
  const [chatSeed, setChatSeed] = useState<ChatSeedInput | null>(null);

  useEffect(() => {
    if (personaId) setPersona(personaId);
  }, [personaId, setPersona]);

  useEffect(() => {
    if (!clientUid || !personaId) return;
    let cancelled = false;
    setLoading(true);
    creativeWorkbenchApi
      .getPerformancePersonaDetail(personaId, {
        clientUid,
        dateFrom: dateRange.from,
        dateTo: dateRange.to,
      })
      .then((p) => {
        if (!cancelled) setData(p);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientUid, personaId, dateRange.from, dateRange.to]);

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/creative/performance')}
            className="p-1.5 text-slate-500 hover:text-teal-600 rounded hover:bg-slate-100"
            aria-label="Back"
          >
            <ArrowLeft size={16} />
          </button>
          <div>
            <div className="text-[10px] font-mono uppercase text-slate-400">
              Persona detail
            </div>
            <h1 className="text-xl font-bold text-navy-900 flex items-center gap-2">
              <Users size={18} className="text-teal-500" />
              {data?.persona_name ?? personaId}
            </h1>
          </div>
        </div>
      </header>

      {loading && !data && (
        <div className="text-sm text-slate-400">Loading persona detail...</div>
      )}

      {data && (
        <>
          {/* Headline KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Tile label="Active ads" value={data.headline.active_ads.toLocaleString()} />
            <Tile label="Visitors" value={data.headline.visitors.toLocaleString()} />
            <Tile
              label="Customers"
              value={data.headline.total_customers.toLocaleString()}
            />
            <Tile
              label="Avg LTV"
              value={CURRENCY.format(data.headline.avg_ltv)}
              accent
            />
            <Tile
              label="Avg match score"
              value={`${data.headline.avg_match_score.toFixed(1)}%`}
            />
          </div>

          {/* Alignment proof */}
          <section className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
            <header className="flex items-center justify-between">
              <h2 className="text-sm font-bold text-navy-900 flex items-center gap-1.5">
                <Sparkles size={14} className="text-teal-500" />
                Alignment Proof
              </h2>
              <button
                onClick={() =>
                  setChatSeed({
                    question: `Why does aligned creative outperform misaligned for ${data.persona_name}?`,
                    persona_id: personaId,
                    chart_axis: { series: 'alignment', dimension: 'persona' },
                  })
                }
                className="text-[10px] font-mono text-teal-600 hover:text-teal-700"
              >
                Ask the analyst →
              </button>
            </header>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <div className="text-[10px] font-mono uppercase text-emerald-700">
                  Aligned spend
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-emerald-700">
                    {CURRENCY.format(data.alignment.spend_aligned)}
                  </span>
                  <span className="text-sm text-emerald-600 font-mono">
                    @ {data.alignment.ltv_roas_aligned.toFixed(2)}x
                  </span>
                </div>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="text-[10px] font-mono uppercase text-red-700">
                  Misaligned spend
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-red-700">
                    {CURRENCY.format(data.alignment.spend_misaligned)}
                  </span>
                  <span className="text-sm text-red-600 font-mono">
                    @ {data.alignment.ltv_roas_misaligned.toFixed(2)}x
                  </span>
                </div>
              </div>
            </div>
            {data.alignment.spend_misaligned > 0 && (
              <p className="text-[11px] text-slate-500 italic">
                Reallocating misaligned spend at the aligned ROAS rate would
                produce roughly{' '}
                <span className="font-bold text-navy-900">
                  {CURRENCY.format(
                    data.alignment.spend_misaligned * data.alignment.ltv_roas_aligned -
                      data.alignment.spend_misaligned * data.alignment.ltv_roas_misaligned,
                  )}
                </span>{' '}
                in additional LTV.
              </p>
            )}
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Ad list */}
            <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-4 space-y-3">
              <header className="flex items-center justify-between">
                <h2 className="text-sm font-bold text-navy-900 flex items-center gap-1.5">
                  <Target size={14} className="text-teal-500" />
                  Ads attributed to this persona
                </h2>
                <span className="text-[10px] font-mono text-slate-400">
                  {data.ads.length} ads
                </span>
              </header>
              <div className="overflow-x-auto">
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="text-left text-slate-400 border-b border-slate-100">
                      <th className="font-mono uppercase pr-2 py-1">Ad</th>
                      <th className="font-mono uppercase px-2 py-1 text-right">Spend</th>
                      <th className="font-mono uppercase px-2 py-1 text-right">
                        LTV ROAS
                      </th>
                      <th className="font-mono uppercase px-2 py-1 text-center">Fit</th>
                      <th className="font-mono uppercase px-2 py-1 text-center">
                        On target
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.ads.slice(0, 30).map((ad) => (
                      <PersonaAdRow
                        key={ad.ad_id}
                        ad={ad}
                        onClick={() => setDrawerAdId(ad.ad_id)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Top formulas */}
            <FormulaAffinityPanel onDrillDown={setChatSeed} limit={6} />
          </div>
        </>
      )}

      {drawerAdId && (
        <AdDetailDrawer
          adId={drawerAdId}
          onClose={() => setDrawerAdId(null)}
          onDrillDown={setChatSeed}
        />
      )}
      {chatSeed && (
        <div className="fixed bottom-4 right-4 w-96 z-30">
          <EmbeddedChatPanel seed={chatSeed} onClose={() => setChatSeed(null)} />
        </div>
      )}
    </div>
  );
}

function Tile({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3">
      <div className="text-[10px] font-mono uppercase text-slate-400">{label}</div>
      <div
        className={clsx(
          'text-2xl font-bold tabular-nums',
          accent ? 'text-teal-600' : 'text-navy-900',
        )}
      >
        {value}
      </div>
    </div>
  );
}

function PersonaAdRow({
  ad,
  onClick,
}: {
  ad: WorkbenchPersonaDetailAd;
  onClick: () => void;
}) {
  return (
    <tr
      onClick={onClick}
      className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer"
    >
      <td className="pr-2 py-2 max-w-xs">
        <div className="font-medium text-navy-900 truncate" title={ad.ad_name ?? ad.ad_id}>
          {ad.ad_name ?? ad.ad_id}
        </div>
        <div className="text-[10px] text-slate-400 truncate">
          {ad.hook_type} · {ad.tone} · {ad.visual_style}
        </div>
      </td>
      <td className="px-2 py-2 text-right font-mono text-navy-900">
        {CURRENCY.format(ad.spend)}
      </td>
      <td className="px-2 py-2 text-right font-mono text-teal-600 font-bold">
        {ad.ltv_roas.toFixed(2)}x
      </td>
      <td className="px-2 py-2 text-center">
        <span
          className={clsx(
            'inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase',
            VERDICT_BADGE[ad.verdict],
          )}
        >
          {ad.fit_score.toFixed(1)}
        </span>
      </td>
      <td className="px-2 py-2 text-center text-[10px]">
        {ad.is_on_target ? (
          <span className="text-emerald-600">✓</span>
        ) : (
          <span className="text-slate-300">—</span>
        )}
      </td>
    </tr>
  );
}
